import { Reveal } from "@/lib/motion";
import { BOOK_REVIEW_MAILTO } from "@/lib/brand";
import FounderNote from "@/sections/FounderNote";
import Timeline from "@/sections/Timeline";

const VALUES = [
  ["Practical", "We work with the business that exists, not the business in a slide deck."],
  ["Structured", "Clarity over cleverness. Documented systems over heroics."],
  ["Quietly modern", "AI and automation where they help. Not where they perform."],
  ["Trustworthy", "Founder-led. Long-game. Plain English."],
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-32 md:pt-40">
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div className="overline">About</div>
        </Reveal>
        <Reveal delay={1}>
          <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tightest max-w-5xl">
            A practical partner for businesses that
            <em className="font-light text-ink-soft"> want to grow on purpose.</em>
          </h1>
        </Reveal>
      </section>

      <section className="mt-20 md:mt-24 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <div className="relative aspect-[4/3] bg-bg-muted overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1000&q=80&auto=format&fit=crop"
              alt="Notebook, pen and coffee on a working desk"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 text-bg-base">
              <div className="overline text-bg-base text-[10px]">Based in</div>
              <div className="font-serif-display text-xl mt-1">Sydney, NSW</div>
            </div>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={1}>
          <div className="overline">The story</div>
          <p className="mt-6 text-xl md:text-2xl text-ink leading-snug font-light max-w-2xl">
            ETradingGroup grew out of a simple observation: most businesses aren't
            short on tools, ideas or ambition. They're short on
            <em className="text-ink-soft"> structure</em> — the connective tissue
            between offer, communication, workflow and admin.
          </p>
          <p className="mt-6 text-[16.5px] text-ink/75 leading-relaxed max-w-2xl">
            We've spent years working across corporate administration, marketing
            communications, research and analytics, operations, technology adoption
            and practical AI implementation. ETradingGroup is where those
            disciplines come together — a quiet, structured practice helping
            practical businesses modernise without losing what makes them work.
          </p>
          <p className="mt-6 text-[16.5px] text-ink/75 leading-relaxed max-w-2xl">
            Specialist offers like 2Tap and our Contracts Administration Services
            are examples of how we think — taking a real-world workflow and
            redesigning it with structure, communication and AI support that
            actually fits the people doing the job.
          </p>
        </Reveal>
      </section>

      <Timeline />

      <section className="mt-20 md:mt-24 border-y border-ink/10 bg-bg-muted py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline">What we value</div>
            <h2 className="mt-6 font-serif-display text-4xl md:text-6xl tracking-tight leading-[0.95] max-w-3xl">
              How we choose to work.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
            {VALUES.map(([t, b], i) => (
              <Reveal
                key={t}
                delay={i}
                className="bg-bg-base p-8 md:p-12"
              >
                <div data-testid={`value-${i + 1}`}>
                  <div className="overline text-brand-red">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-5 font-serif-display text-3xl md:text-4xl tracking-tight">
                    {t}
                  </h3>
                  <p className="mt-4 text-ink/75 leading-relaxed text-[15.5px] max-w-md">
                    {b}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FounderNote />

      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Ready to reimagine
              <em className="font-light text-ink-soft"> how your business works?</em>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-4 lg:justify-self-end" delay={1}>
            <a
              href={BOOK_REVIEW_MAILTO}
              data-testid="about-cta"
              className="inline-flex items-center gap-3 bg-brand-red hover:bg-brand-redHover text-white px-7 py-4 text-[13.5px] tracking-[0.06em] font-medium transition-colors"
            >
              Book a Review
              <span aria-hidden>→</span>
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
